import { Component, OnInit, Input, ElementRef, ViewChild } from '@angular/core';
import { GfTitleService } from '../../../services/core.title.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UserService } from '../../../services/core.user.service';
import { UiService } from '../../../services/core.ui.service';
import { ConstantsService } from '../../../services/core.constants.service';
import { StateService } from '@uirouter/angular';
import { ChangeEmailRequestModalComponent } from '../changeemail/request/changeemail.request.modal.component';
import { UserDeleteModalComponent } from '../delete-user/delete.user.modal.component';
import * as _ from 'lodash';

@Component({
  selector: 'user-profile-edit',
  template: require('./user.edit-profile.component.html'),
})
export class UserProfileEditComponent implements OnInit {
  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef;
  @Input() user: any;
  @Input() siteConfig: any;

  userData = {
    id: '',
    firstname: '',
    lastname: '',
    email: '',
    unixName: '',
    address: '',
    address2: '',
    phone: '',
    fax: '',
    useCustomAvatar: '',
    timezone: { label: ''},
    language: '',
    customAvatar: null,  // holds the File when user selects it
    ccode: { id: '' }
  };

  options: any = {
    language: this.constantsService.languages,
    country: this.constantsService.countries,
    timezone: this.constantsService.timezones
  };

  avatarPreview = '';
  confirming = false;
  saving = false;
  status = 0;
  msg = '';
  canDelete = false;

  constructor(
    private userService: UserService,
    private uiService: UiService,
    private constantsService: ConstantsService,
    private modalService: NgbModal,
    private stateService: StateService, 
    
    private titleService: GfTitleService,
    ) {}

  ngOnInit(): void {
    this.setTitle();
    this.setScopeUserData();
    this.checkDeletePermission();
  }

  triggerFileInput() {
    this.fileInput.nativeElement.click();
  }

  private setTitle(): void {
    this.titleService.setTitle(this.userData.unixName, 'Edit Profile');
  }

  private setScopeUserData(): void {
    // Copy values from @Input() user to this.userData
    Object.keys(this.user).forEach((key) => {
      this.userData[key] = this.user[key];
    });

    // Match up ccode/timezone/language with the relevant objects
    this.userData.ccode = _.find(this.options.country.fieldElements, { id: this.userData.ccode });
    this.userData.timezone = _.find(this.options.timezone.fieldElements, { id: this.userData.timezone });
    this.userData.language = _.get(
      _.find(this.options.language.fieldElements, { id: this.userData.language }),
      'id',
      'en'
    );

    this.avatarPreview = this.getCustomAvatarUrl();
  }

  cancel(): void {
    this.setScopeUserData();
  }

  setLanguage(lang: string): void {
    this.userData.language = lang;
  }

  optionButtonClass(isActive: boolean, activeClass = 'btn-primary', inactiveClass = 'btn-default'): string {
    return isActive ? activeClass : inactiveClass;
  }

  /**
   * Submit the form in two steps:
   * 1) Post user data (JSON) first.
   * 2) If successful, check if there's a customAvatar file. If so, upload it (multipart post).
   */
  submit(): void {
    this.saving = true;

    // 1) Gather JSON data, excluding the raw File if needed
    const updateData = this.getSaveUserData();
    const jsonData = { ...updateData };
    delete jsonData.file;

    // 2) Post user data (JSON-only):
    this.userService.profile
      .save(jsonData)
      .then((response: any) => {
        if (response.success) {
          // Update local user data
          this.updateUserData(jsonData);

          // If we have a file in this.userData.customAvatar, do the second call
          if (this.userData.customAvatar) {
            return this.userService.profile
              .uploadAvatar(this.userData.unixName, this.userData.customAvatar)
              .then((uploadResponse: any) => {
                // If success
                if (uploadResponse.success) {
                  this.uiService.message.showSuccess('Avatar updated successfully', 'Success');
                } else {
                  this.uiService.message.showError('Failed to upload avatar', 'Error');
                }
                return uploadResponse;
              })
              .catch((err) => {
                this.uiService.message.showError('Failed to upload avatar', 'Error');
                return { success: false, errorMessage: err };
              });
          } else {
            // No file? Then just resolve the original success
            return response;
          }
        } else {
          // If the first JSON (user profile data) call fails, stop here
          this.uiService.message.showError('Failed to update user data', 'Error');
          return response;
        }
      })
      .catch((error: any) => {
        // If the first call throws an error
        this.uiService.message.showError('Error saving user data', 'Error');
        console.error(error);
        return { success: false, error };
      })
      .then((finalResult) => {
        this.saving = false;
        // Show final success if both calls succeeded
        if (finalResult?.success) {
          this.uiService.message.showSuccess('User profile updated successfully.', 'Success');
        }
      });
  }

  onChangeCustomAvatar(newValue: string): void {
    this.userData.useCustomAvatar = newValue;
    this.avatarPreview = this.userData.useCustomAvatar === 'N'
      ? '/core/img/default-avatar.png'
      : this.getCustomAvatarUrl();
  }

  getPreview(file: File): void {
    const fileReader = new FileReader();
    fileReader.onload = (event: any) => {
      this.avatarPreview = event.target.result;
    };
    fileReader.readAsDataURL(file);
  }

  fileSelected(event: any): void {
    const file: any = _.first(event.target.files);
    if (!file) return;
    this.getPreview(file);
    this.userData.customAvatar = file; // store the File
    this.userData.useCustomAvatar = 'Y';
  }

  openEmailChangeModal(): void {
    const modalRef = this.modalService.open(ChangeEmailRequestModalComponent);
    modalRef.componentInstance.onClose.subscribe(() => {
      modalRef.close();
    });
  }

  openUserDeleteModal(): void {
    const modalRef = this.modalService.open(UserDeleteModalComponent);
    modalRef.componentInstance.user = this.user;
    modalRef.componentInstance.onClose.subscribe(() => {
      modalRef.close();
    });
    modalRef.componentInstance.onDelete.subscribe(() => {
      this.deleteUser();
      modalRef.close();
    });
  }

  deleteUser(): void {
    this.userService.deleteUser(this.user.unixName)
    .then(() => {
        this.uiService.message.showSuccess("This account has been deleted");
        if (!this.userService.session.isSiteAdmin()) {
          this.userService.session.logout().then(() => {
            this.stateService.go('home.user');
          });
        }
    },
    () => {
    this.uiService.message.showError("Failed to delete user account");
    });
  }

  private getCustomAvatarUrl(): string {
    return `/api/user/${this.user.unixName}/avatar`;
  }

  private getSaveUserData(): any {
    return {
      id: this.userData.id,
      firstname: this.userData.firstname,
      lastname: this.userData.lastname,
      unixName: this.userData.unixName,
      address: this.userData.address,
      address2: this.userData.address2,
      phone: this.userData.phone,
      fax: this.userData.fax,
      timezone: this.userData.timezone?.label,
      ccode: this.userData.ccode?.id,
      language: this.userData.language,
      useCustomAvatar: this.userData.useCustomAvatar,
      file: this.userData.customAvatar, // We'll remove it when sending JSON
    };
  }

  private updateUserData(updatedData: any): void {
    // Update local user model or whatever is necessary
    Object.assign(this.userService.profile, updatedData);
  }

  private checkDeletePermission(): void {
    const isOwner = this.userService.session.getUserId() === this.userData.id;
    const isSiteAdmin = this.userService.session.isSiteAdmin();
    this.canDelete = isOwner || isSiteAdmin;
  }
}
